/**
 * The places opened on this device, most recent first.
 *
 * This is a convenience, not trip data: it never leaves the phone, it is not shared with the other
 * travellers, and losing it costs nothing. So it lives in `localStorage` and every read and write is
 * wrapped — a device with storage blocked or full simply has no history, which the search surface
 * already copes with by falling through to the nearest places.
 *
 * Kept per trip, because "the last place I opened" means nothing across two different holidays.
 */

/** How many are kept. Enough to be useful on the surface without pushing the nearest off it. */
export const RECENT_KEPT = 5;

const keyFor = (tripId: string) => `trip.recent-places.${tripId}`;

function stored(tripId: string): string[] {
  try {
    const raw = localStorage.getItem(keyFor(tripId));
    const parsed: unknown = raw === null ? [] : JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

/** Puts a place at the front, without ever listing it twice. */
export function rememberOpened(tripId: string, poiId: string): void {
  try {
    const rest = stored(tripId).filter((id) => id !== poiId);
    localStorage.setItem(keyFor(tripId), JSON.stringify([poiId, ...rest].slice(0, RECENT_KEPT)));
  } catch {
    // Storage refused. The history is a nicety; the search works without it.
  }
}

/**
 * The remembered places that the trip still has, in the order they were opened. A place deleted
 * since — or one belonging to a list this device no longer holds — is dropped rather than shown as
 * a row that cannot be opened.
 */
export function recentlyOpened<T extends { id: string }>(tripId: string, pois: T[]): T[] {
  const byId = new Map(pois.map((p) => [p.id, p]));
  return stored(tripId)
    .map((id) => byId.get(id))
    .filter((p): p is T => p !== undefined);
}
